import React from 'react';
import { connect } from 'react-redux';
import { Field, reduxForm, formValueSelector, change } from 'redux-form';
import PropTypes from 'prop-types';
import { map } from 'lodash';
import ContractCheckoutValidator from '../../../common/validation/ContractCheckoutValidator';
import { CheckoutSelector } from '../../../redux/reducers/CheckoutReducer';
import FormSelect from '../../form/FormSelect/FormSelect';
import HousingStatuses from './HousingStatuses.json';
import EmploymentStatuses from './CAOccupation.json';
import CustomField from '../../form/CustomField';
import FormConfigUtils from '../../form/FormConfigUtils';
import FormHiddenContainer from '../../../redux/containers/FormHiddenContainer';
import CreditAssessmentErrorMessage from '../CreditAssessmentErrorMessage/CreditAssessmentErrorMessage';
import NextButton from '../NextButton/NextButton';
import handleSubmitFail from '../SubmitFailHandler';
import Configuration from '../../../common/Configuration.json';
import CaAddressDetails from './CaAddressDetails';

const clearCustomField = (dispatch, field) => {
  if (field.fieldType === 'month-year') {
    dispatch(change('checkout', `${field.name}Month`, ''));
    dispatch(change('checkout', `${field.name}Year`, ''));
  } else {
    dispatch(change('checkout', field.name, ''));
  }
};

const resetEmploymentStatus = dispatch => (value) => {
  const newSubcat = FormConfigUtils.getSubCategories(EmploymentStatuses, value);
  // If the selected occupation status has only 1 employment status push that status straight to redux.
  if (newSubcat && newSubcat.length === 1) {
    dispatch(change('checkout', 'creditAssessment.employmentStatus', newSubcat[0].value));
  } else {
    // Otherwise clear the employment status as we don't want to show any previously selected
    // value from a different occupation status.
    dispatch(change('checkout', 'creditAssessment.employmentStatus', ''));
  }
  // Clear any customer fields that we added to the page under a previous occupation status.
  // ToDo: This only works because all the custom fields are defined under the occupation status E.
  // We should really be getting all the previously displayed custom fields and clearing them.
  const customFields = FormConfigUtils.getCustomFieldsInCategory(EmploymentStatuses, 'E');
  if (customFields) {
    for (const field of customFields) {
      clearCustomField(dispatch, field);
    }
  }
  return value;
};

const handleClick = handleSwitchToOpenTerm => (event) => {
  event.preventDefault();
  handleSwitchToOpenTerm();
};

const CreditAssessmentStep2 = (props) => {
  const {
    dispatch, modemPrice, connectionFee, occupationStatus,
    caSubmitting, caError, handleSubmit, handleNext, handlePrevious, handleSwitchToOpenTerm
  } = props;
  const subcategories = FormConfigUtils.getSubCategories(EmploymentStatuses, occupationStatus);
  return (
    <section>
      <form onSubmit={handleSubmit(handleNext)}>
        <div className="container">
          <div className="row">
            <div className="col-xs-12">
              <p>
                Getting over all these questions? You can skip the credit assessment by&nbsp;
                <a href="/checkout" onClick={handleClick(handleSwitchToOpenTerm)}>
                  signing up for a No Pesky Term contract
                </a>
                . Please note a ${modemPrice} modem fee (unless you BYO modem) and ${connectionFee} connection fee will apply on the No Pesky
                Term contract.
              </p>
            </div>
          </div>
          <CaAddressDetails {...props} />
          <div className="row">
            <div className="col-xs-12">
              <Field
                label="Housing Status"
                id="creditAssessment.housingStatus"
                name="creditAssessment.housingStatus"
                autoTestClass="auto-test-housing-status"
                component={FormSelect}
                required
              >
                {map(HousingStatuses, status => (
                  <option
                    value={status.value}
                    key={status.id}
                  >{status.name}</option>
                ))}
              </Field>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12">
              <Field
                label="Occupation Status"
                id="creditAssessment.occupationStatus"
                name="creditAssessment.occupationStatus"
                autoTestClass="auto-test-occupation-status"
                component={FormSelect}
                normalize={resetEmploymentStatus(dispatch)}
                required
              >
                {map(EmploymentStatuses, status => (
                  <option
                    value={status.value}
                    key={status.id}
                  >{status.name}</option>
                ))}
              </Field>
            </div>
          </div>
          {map(FormConfigUtils.getCustomFieldsInCategory(
            EmploymentStatuses, occupationStatus), field => (
              <div key={field.id} className="row">
                <div className="col-sm-12">
                  <CustomField {...field} {...props} />
                </div>
              </div>
            )
          )}
          {subcategories && subcategories.length === 1 &&
            <FormHiddenContainer name="creditAssessment.employmentStatus" />
          }
          {subcategories && subcategories.length > 1 &&
          <div className="row">
            <div className="col-xs-12">
              <Field
                label="Employment Status"
                id="creditAssessment.employmentStatus"
                name="creditAssessment.employmentStatus"
                autoTestClass="auto-test-employment-status"
                component={FormSelect}
                required
              >
                {map(subcategories, status => (
                  <option value={status.value} key={status.value}>{status.name}</option>
                ))}
              </Field>
            </div>
          </div>
          }
        </div>
        <CreditAssessmentErrorMessage caError={caError} />
        <NextButton
          submitting={caSubmitting}
          disabled={caSubmitting}
          handlePrevious={handlePrevious}
        />
      </form>
    </section>
  );
};

const formSelector = formValueSelector('checkout');
const mapStateToProps = state => ({
  modemPrice: Configuration.modemPrice,
  connectionFee: Configuration.connectionFee,
  occupationStatus: formSelector(state, 'creditAssessment.occupationStatus'),
  employmentStatus: formSelector(state, 'creditAssessment.employmentStatus'),
  caError: CheckoutSelector.getCaError(state),
  caSubmitting: CheckoutSelector.getCaSubmitting(state)
});

CreditAssessmentStep2.propTypes = {
  modemPrice: PropTypes.number,
  connectionFee: PropTypes.number,
  occupationStatus: PropTypes.string,
  caError: PropTypes.object,
  caSubmitting: PropTypes.bool,
  dispatch: PropTypes.func,
  handleSubmit: PropTypes.func,
  handleNext: PropTypes.func,
  handlePrevious: PropTypes.func,
  handleSwitchToOpenTerm: PropTypes.func
};

export default connect(mapStateToProps)(reduxForm({
  form: 'checkout', // Form name is same
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,  // unregister fields on unmount
  validate: ContractCheckoutValidator,
  onSubmitFail: err => handleSubmitFail(err)
})(CreditAssessmentStep2));



/** WEBPACK FOOTER **
 ** ./src/components/checkout/CreditAssessmentStep2/CreditAssessmentStep2.js
 **/